// Small, pure rules for how an Arc answer's document references read on screen
// (kept apart from ChatMessage.jsx so they can be run on their own).

/** A reference that only says "this document": no page, no time, no passage. */
export function isDocumentLevel(ref) {
  return !!ref && ref.page == null && ref.timestamp == null && !ref.snippet && !ref.chunk_id;
}

/** Does the answer already name this reference's document (its file name, or the name without the extension)? */
export function namedInText(ref, lowerText) {
  const name = String(ref?.filename || '').toLowerCase();
  if (!name || !lowerText) return false;
  if (lowerText.includes(name)) return true;
  const stem = name.replace(/\.[^.]+$/, '');
  return stem.length >= 8 && lowerText.includes(stem);
}

/**
 * A numbered marker is redundant when all it adds is a second link to a document the
 * sentence already names — "The document [Letter.pdf] has been moved to /Hearing binder[1]"
 * read on screen as the folder name followed by a second copy of the document's link.
 * A marker that points INTO a document (a page, a passage) is never redundant.
 */
export function isRedundantMarker(ref, lowerText) {
  return isDocumentLevel(ref) && namedInText(ref, lowerText);
}

/**
 * "[" + a link + "]" → the link. The model wrote a file name in square brackets and the
 * name itself became the link; the brackets left around it are noise.
 */
export function unwrapBracketedLinks(markdown) {
  return String(markdown || '').replace(/\[(\[[^\]]*\]\([^)]*\))\]/g, '$1');
}

/**
 * Replace the numbered markers of an answer — [N], [N, M] and [N-M] — with whatever
 * `linksFor(numbers)` gives: `{ links: [markdown…], known: bool }`. A marker with links
 * becomes them; one whose references are known but give no link (a duplicate, or a
 * redundant "this document") disappears together with the space before it, so the
 * sentence does not end " ."; one that names no known reference is left as written.
 */
export function replaceMarkers(text, linksFor) {
  return String(text || '').replace(/([ \t]*)\[(\d+(?:\s*-\s*\d+|(?:\s*,\s*\d+)+)?)\]/g, (match, lead, body) => {
    let nums = [];
    const range = body.match(/^\s*(\d+)\s*-\s*(\d+)\s*$/);
    if (range) {
      const lo = parseInt(range[1], 10);
      const hi = parseInt(range[2], 10);
      if (!(Number.isFinite(lo) && Number.isFinite(hi) && hi >= lo && hi - lo < 50)) return match; // pathological range
      for (let i = lo; i <= hi; i += 1) nums.push(i);
    } else {
      nums = body.split(',').map((x) => parseInt(x.trim(), 10)).filter(Number.isFinite);
    }
    const out = linksFor(nums) || { links: [], known: false };
    if (!out.links || out.links.length === 0) return out.known ? '' : match;
    return lead + out.links.join(' ');
  });
}
